import { useEffect, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useManagementWording } from "../lib/useManagementWording";
import type { PipelineHealthManagementAssessment } from "./managementAssessment";
import { displayedManagementReadout, managementReadoutPacket, managementWordingSlots, PIPELINE_HEALTH_READOUT_PROMPT } from "./readoutComposition";
import { MANAGEMENT_COPY as C } from "./managementCopy";

type CopyState = ReturnType<typeof displayedManagementReadout> & { loading: boolean; error: unknown; retry: () => void };

/** One request writes the Readout and three cards, with a single app-owned
 * assessment. No competing prompts or independent AI-selected priorities. */
export function PipelineHealthWording({ assessment, ready, children }: {
  assessment: PipelineHealthManagementAssessment; ready: boolean; children: (state: CopyState) => ReactNode;
}) {
  const wording = useManagementWording({ namespace: "pipeline-health-management-language-v2", packet: managementReadoutPacket(assessment),
    question: PIPELINE_HEALTH_READOUT_PROMPT, slots: managementWordingSlots(assessment), ready });
  useEffect(() => {
    if (!import.meta.env?.DEV || !wording.error) return;
    const error = wording.error as { code?: unknown; status?: unknown; name?: unknown };
    // Local diagnostics only: never log response bodies, prose, facts or tokens.
    console.warn("Pipeline Health composition unavailable", JSON.stringify({
      code: typeof error.code === "string" && /^(?:chat_[a-z_]+|access_denied|rate_limited|invalid_request)$/u.test(error.code) ? error.code : "unclassified",
      status: typeof error.status === "number" && error.status >= 100 && error.status < 600 ? error.status : null,
      type: ["Error", "TypeError", "AbortError"].includes(String(error.name)) ? error.name : "unknown",
    }));
  }, [wording.error]);
  const displayed = displayedManagementReadout(assessment, wording.values ? JSON.stringify(wording.values) : undefined);
  return children({ ...displayed, loading: wording.loading, error: wording.error, retry: wording.retry });
}

export function PipelineHealthReadout({ assessment, wording, onInspect }: {
  assessment: PipelineHealthManagementAssessment; wording: CopyState; onInspect: () => void;
}) {
  return <section aria-labelledby="management-readout" className="space-y-4" data-copy-source={wording.composed ? "openai" : "computed"}>
    <h2 id="management-readout" className="text-base font-medium text-[#231942]">{C.title}</h2>
    <p className="max-w-[85ch] text-base leading-7 text-gray-700">{wording.text}</p>
    <button type="button" onClick={onInspect} className="text-sm text-gray-600 underline decoration-dotted underline-offset-4">{assessment.inspectLabel}</button>
    {!wording.composed && <div className="flex items-center gap-3 text-xs text-gray-500" role="status">
      <span>{wording.loading ? C.preparing : C.fallback}</span>
      {Boolean(wording.error) && !wording.loading && <Button size="sm" variant="ghost" onClick={wording.retry}>{C.retry}</Button>}
    </div>}
  </section>;
}
